import React, { useEffect, useMemo, useState } from 'react';
import { useRecentPurchases } from 'src/queries/loyalty';
import { Typography } from '@mui/material';
import { Column, DataTable, Pager, SearchField } from './DataTable';
import { Panel } from './Panel';
import { DateRange, PeriodKey, toDateInput } from './period';
import { bodyFont, customerName, formatCount, formatDateTime, ink } from './tokens';

interface PurchaseRow {
  id: number;
  user: { id: number; first_name: string; last_name: string };
  scanner?: { id: number; first_name: string; last_name: string };
  date_created: string;
}

const PAGE_SIZE = 8;

/**
 * The stamp-by-stamp log for the selected period, with the staff member who
 * scanned each one.
 *
 * Unlike the charts, this reads from `loyalty/recent-purchases` rather than the
 * page's stats payload: that endpoint is the only one that carries the scanner,
 * which is what makes the log useful for settling "who served this customer".
 */
export const RecentStampsTable: React.FC<{
  merchantId: number;
  range: DateRange;
  period: PeriodKey;
  rangeLabel: string;
}> = ({ merchantId, range, period, rangeLabel }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  // Depend on the formatted bounds, not the Date objects: the page rebuilds
  // those on every render, and re-fetching on every render would hammer the API.
  const startDate = toDateInput(range.start);
  const endDate = toDateInput(range.end);

  // The range is part of the query key, so flipping period and flipping back is
  // served from cache rather than refetched.
  const {
    data: rows = [],
    isFetching: loading,
    isError,
  } = useRecentPurchases<PurchaseRow>(
    merchantId,
    period === 'all' ? undefined : startDate,
    period === 'all' ? undefined : endDate
  );

  const error = isError ? 'We could not load recent stamps.' : null;

  useEffect(() => {
    setPage(0);
  }, [search, startDate, endDate, period]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      customerName(row.user?.first_name, row.user?.last_name).toLowerCase().includes(query)
    );
  }, [rows, search]);

  const columns: Column<PurchaseRow>[] = [
    {
      key: 'customer',
      label: 'Customer',
      width: 'minmax(0,1.3fr)',
      render: (row) => (
        <Typography
          noWrap
          sx={{ fontFamily: bodyFont, fontWeight: 500, fontSize: 14, color: ink.strong }}
        >
          {customerName(row.user?.first_name, row.user?.last_name)}
        </Typography>
      )
    },
    {
      key: 'date',
      label: 'When',
      width: 'minmax(0,1.3fr)',
      render: (row) => (
        <Typography noWrap sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.body }}>
          {formatDateTime(row.date_created)}
        </Typography>
      )
    },
    {
      key: 'scanner',
      label: 'Scanned by',
      width: 'minmax(0,1fr)',
      render: (row) => (
        <Typography
          noWrap
          sx={{
            fontFamily: bodyFont,
            fontSize: 14,
            color: row.scanner ? ink.body : ink.faint
          }}
        >
          {row.scanner ? customerName(row.scanner.first_name, row.scanner.last_name) : '—'}
        </Typography>
      )
    }
  ];

  return (
    <Panel
      title='Recent stamps'
      subtitle={error ?? (loading ? 'Loading…' : `${formatCount(rows.length)} in ${rangeLabel}`)}
      hint='Every stamp scanned in the selected period, newest first.'
      action={<SearchField value={search} onChange={setSearch} placeholder='Search by name' />}
    >
      <DataTable
        columns={columns}
        rows={filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage={
          error
            ? 'Recent stamps are unavailable right now.'
            : search.trim()
              ? `No stamps match “${search.trim()}”.`
              : 'No stamps in this period.'
        }
      />
      {!loading && filtered.length > 0 && (
        <Pager
          page={page}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onPageChange={setPage}
          noun='stamps'
        />
      )}
    </Panel>
  );
};

export default RecentStampsTable;
